import { useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { Notice } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ApiError } from "@/lib/apiClient";
import { merchantPdfImport } from "@/features/imports/api/imports.api";
import { users } from "@/features/users/api/users.api";
import { UserSelect } from "@/features/users/components/UserSelect";

type PdfField = { key: string; label: string; to?: string };
const GROUPS: { title: string; fields: PdfField[] }[] = [
  { title: "Business", fields: [
    { key: "dbaName", label: "DBA / business name *", to: "Merchant.DbaName" },
    { key: "legalName", label: "Legal name", to: "Merchant.LegalName" },
    { key: "mcc", label: "MCC", to: "Merchant.Mcc" },
    { key: "ownershipType", label: "Ownership type" },
  ]},
  { title: "Address (DBA / physical)", fields: [
    { key: "addressLine1", label: "Address", to: "Merchant.AddressLine1" },
    { key: "city", label: "City", to: "Merchant.City" },
    { key: "state", label: "State", to: "Merchant.State" },
    { key: "zip", label: "ZIP", to: "Merchant.Zip" },
  ]},
  { title: "Contact", fields: [
    { key: "phone", label: "Phone", to: "Merchant.Phone" },
    { key: "email", label: "Email", to: "Merchant.Email" },
    { key: "contactName", label: "Contact name", to: "Merchant.ContactName" },
    { key: "contactTitle", label: "Contact title" },
    { key: "contactPhone", label: "Contact phone", to: "Principal.Phone" },
  ]},
  { title: "Tax / W-9", fields: [
    { key: "taxId", label: "Tax ID", to: "Merchant.TaxId / Ein / Ssn" },
    { key: "taxIdType", label: "Tax ID type (EIN/SSN/ITIN)", to: "drives Ein vs Ssn" },
  ]},
  { title: "Principal owner", fields: [
    { key: "ownerFirstName", label: "Owner first name", to: "Principal.FirstName" },
    { key: "ownerLastName", label: "Owner last name", to: "Principal.LastName" },
    { key: "ownerTitle", label: "Owner title", to: "Principal.Title" },
    { key: "ownerSsn", label: "Owner SSN", to: "Merchant.Ssn" },
    { key: "ownerDob", label: "Owner DOB" },
    { key: "ownerPercent", label: "Owner % owned", to: "Principal.Ownership%" },
  ]},
  { title: "Banking", fields: [
    { key: "bankName", label: "Bank name" },
    { key: "routingNumber", label: "Routing number", to: "Merchant.BankRoutingNumber" },
    { key: "accountNumber", label: "Account number", to: "Merchant.BankAccountNumber" },
    { key: "accountType", label: "Account type" },
  ]},
  { title: "Credit card processing methods", fields: [
    { key: "pctSwiped", label: "Card swiped %", to: "Merchant.PctCardSwiped" },
    { key: "pctKeyedCardPresent", label: "Keyed card-present %", to: "Merchant.PctKeyedCardPresent" },
    { key: "pctKeyedCardNotPresent", label: "Keyed CNP / MOTO %", to: "Merchant.PctKeyedCardNotPresent" },
    { key: "pctEcommerce", label: "eCommerce %", to: "Merchant.PctEcommerce" },
    { key: "pctB2B", label: "Business-to-business %", to: "Merchant.PctB2B" },
  ]},
  { title: "Projected volume", fields: [
    { key: "avgTicket", label: "Average ticket $", to: "Merchant.AvgTicketAmount" },
    { key: "monthlyVolume", label: "Monthly volume $", to: "Merchant.MonthlyVolumeEstimate" },
  ]},
  { title: "Fee schedule", fields: [
    { key: "discountRate", label: "Discount rate %", to: "Fee sched. Visa/MC/Disc" },
    { key: "amexRate", label: "Amex rate %", to: "Fee sched. Amex" },
    { key: "transactionFee", label: "Per-item / transaction fee", to: "Fee sched. TransactionFee" },
    { key: "authFee", label: "Authorization fee", to: "Fee sched. AuthorizationFee" },
    { key: "voiceAuthFee", label: "Voice auth fee" },
    { key: "batchFee", label: "Batch fee", to: "Fee sched. BatchFee" },
    { key: "monthlyFee", label: "Monthly service fee", to: "Fee sched. MonthlyFee" },
    { key: "monthlyPciFee", label: "Monthly PCI fee", to: "Fee sched. MonthlyPciFee" },
    { key: "monthlyMinimumFee", label: "Monthly minimum", to: "Fee sched. MonthlyMinimumFee" },
    { key: "annualFee", label: "Annual fee", to: "Fee sched. AnnualFee" },
    { key: "chargebackFee", label: "Chargeback fee", to: "Fee sched. ChargebackFee" },
    { key: "retrievalFee", label: "Retrieval fee" },
  ]},
  { title: "Sales rep / dates", fields: [
    { key: "salesRepName", label: "Sales rep (printed name)", to: "Owner agent (matched)" },
    { key: "salesRepDate", label: "Sales rep date / application date", to: "Merchant.ApprovedDate" },
    { key: "assocNumber", label: "Assoc #", to: "Merchant.AssocNumber" },
    { key: "externalId", label: "Application ID", to: "audit log" },
  ]},
];

const MAPPED_KEYS = new Set(GROUPS.flatMap((g) => g.fields.map((fld) => fld.key)));

const NO_OWNER = "__none__";
const NO_OWNER_OPTIONS = [{ value: NO_OWNER, label: "— Select —" }];

export default function ImportMerchantPdfPage() {
  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(null);
  const [scanning, setScanning] = useState(false);
  const [scanned, setScanned] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [f, setF] = useState<Record<string, string>>({});
  const [mid, setMid] = useState("");
  const [ownerId, setOwnerId] = useState("");
  const [agentMatch, setAgentMatch] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const set = (k: string) => (v: string) => setF((p) => ({ ...p, [k]: v }));

  async function onScan() {
    if (!file) return;
    setScanning(true);
    setError(null);
    try {
      const data = await merchantPdfImport.scan(file);
      const next: Record<string, string> = {};
      for (const [k, v] of Object.entries(data)) next[k] = v ?? "";
      setF(next);
      setScanned(true);

      const rep = (next.salesRepName ?? "").trim();
      if (rep) {
        const hit = await users.list({ keyword: rep, pageSize: 10 })
          .then((p) => p.items.find((u) => u.name.trim().toLowerCase() === rep.toLowerCase()) ?? null)
          .catch(() => null);
        if (hit) {
          setOwnerId(hit.id);
          setAgentMatch(`Matched sales rep "${next.salesRepName}" → ${hit.name}`);
        } else {
          setAgentMatch(`No agent matches sales rep "${next.salesRepName}" — pick an owner below.`);
        }
      }
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Couldn't scan that PDF.");
    } finally {
      setScanning(false);
    }
  }

  async function onCreate() {
    setError(null);
    if (!f.dbaName?.trim()) { setError("A DBA / business name is required."); return; }
    if (!ownerId) { setError("Pick an owner for the merchant."); return; }
    setCreating(true);
    try {
      const r = await merchantPdfImport.commit({ ...f, mid: mid.trim(), ownerUserId: ownerId, raw: f });
      navigate(`/merchants/${r.merchantId}`);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Couldn't create the merchant.");
    } finally {
      setCreating(false);
    }
  }

  const extraKeys = Object.keys(f).filter((k) => !MAPPED_KEYS.has(k));

  return (
    <>
      <header className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-800">Import Merchant PDF</h1>
        <p className="text-sm text-slate-500 mt-1">
          Upload a merchant card-processing application (PDF). It's scanned into
          fields you can review and edit, then a new merchant is created from them.
        </p>
      </header>

      {error && <Notice tone="error" className="mb-4">{error}</Notice>}

      <div className="space-y-6">
        <Panel title="1 · Upload the application PDF">
          <div className="flex flex-wrap items-center gap-3">
            <input
              type="file"
              accept="application/pdf,image/*"
              onChange={(e) => { setFile(e.target.files?.[0] ?? null); setScanned(false); }}
              className="text-sm text-slate-700 file:mr-3 file:rounded-md file:border-0 file:bg-slate-800 file:px-3 file:py-1.5 file:text-white file:text-sm hover:file:bg-slate-700"
            />
            <Button type="button" onClick={onScan} disabled={!file || scanning}>
              {scanning ? "Scanning…" : "Scan PDF"}
            </Button>
          </div>
          <p className="text-xs text-slate-400 mt-2">
            Scanning uses the Anthropic key configured in Settings → AI &amp; Scanning.
          </p>
        </Panel>

        {scanned && (
          <>
            <Panel title="2 · Review extracted fields → merchant mapping">
              <p className="text-xs text-slate-500 mb-4">
                Every field the scan found, with where it lands. Fields shown as
                <span className="mx-1 text-slate-400">→ Boarding data</span> have no dedicated
                merchant column but are still saved on the merchant's editable boarding copy, so
                nothing is lost.
              </p>
              {GROUPS.map((g) => (
                <div key={g.title} className="mb-5 last:mb-0">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2 border-b border-slate-100 pb-1">{g.title}</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-4 gap-y-3">
                    {g.fields.map((fld) => (
                      <label key={fld.key} className="block">
                        <span className="flex items-baseline justify-between gap-2 mb-1">
                          <span className="text-xs text-slate-600">{fld.label}</span>
                          <span className="text-[10px] text-slate-400 font-mono whitespace-nowrap">
                            → {fld.to ?? "Boarding data"}
                          </span>
                        </span>
                        <Input value={f[fld.key] ?? ""} onChange={(e) => set(fld.key)(e.target.value)} />
                      </label>
                    ))}
                  </div>
                </div>
              ))}

              {extraKeys.length > 0 && (
                <div className="mb-1">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2 border-b border-slate-100 pb-1">Other extracted</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-4 gap-y-3">
                    {extraKeys.map((k) => (
                      <label key={k} className="block">
                        <span className="flex items-baseline justify-between gap-2 mb-1">
                          <span className="text-xs text-slate-600">{k}</span>
                          <span className="text-[10px] text-slate-400 font-mono">→ Boarding data</span>
                        </span>
                        <Input value={f[k] ?? ""} onChange={(e) => set(k)(e.target.value)} />
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </Panel>

            <Panel title="3 · Accept &amp; create the merchant">
              <p className="text-xs text-slate-400 mb-3">
                Review the fields above, then accept to create the merchant. The MID can be left
                blank — the processor assigns it later. We'll create a temporary
                <code className="mx-1 px-1 rounded bg-slate-100">PENDING-####</code> MID you can
                replace from the merchant page once it's issued.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl">
                <label className="block">
                  <span className="block text-xs text-slate-500 mb-1">MID (optional — fill later)</span>
                  <Input value={mid} onChange={(e) => setMid(e.target.value)} placeholder="Leave blank — assigned by the processor later" />
                </label>
                <label className="block">
                  <span className="block text-xs text-slate-500 mb-1">Owner (agent) — auto-assigned from sales rep *</span>
                  <UserSelect
                    value={ownerId || NO_OWNER}
                    onValueChange={(v) => setOwnerId(v === NO_OWNER ? "" : v)}
                    pinnedOptions={NO_OWNER_OPTIONS}
                  />
                </label>
              </div>
              {agentMatch && (
                <p className={`text-xs mt-2 ${ownerId ? "text-emerald-700" : "text-amber-700"}`}>{agentMatch}</p>
              )}
              <Button type="button" className="mt-4" onClick={onCreate} disabled={creating}>
                {creating ? "Creating…" : "Accept & create merchant"}
              </Button>
            </Panel>
          </>
        )}
      </div>
    </>
  );
}

function Panel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Card>
      <CardHeader className="border-b py-3">
        <CardTitle className="text-base font-semibold text-slate-900">{title}</CardTitle>
      </CardHeader>
      <CardContent className="pt-6">{children}</CardContent>
    </Card>
  );
}
